/**
 * Rejection Reason API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type { BulkResultData, BulkResultResponse } from '../types/bulk';
import type {
  RejectionReason,
  RejectionReasonListResponse,
  RejectionReasonResponse,
  RejectionReasonCreateInput,
  RejectionReasonUpdateInput,
} from '../types/rejection-reason';

interface ListRejectionReasonsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class RejectionReasonService {
  /**
   * List rejection reasons with pagination.
   */
  async listRejectionReasons(
    workspaceId: string,
    idToken: string,
    options: ListRejectionReasonsOptions = {}
  ): Promise<RejectionReasonListResponse> {
    const { page = 1, size = 50, sortBy = 'reason', order = 'asc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<RejectionReasonListResponse>(`/rejection-reasons`, {
      params: { skip, limit: size, sort_by: sortBy, order, search },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list rejection reasons');
    }

    return response.data;
  }

  /**
   * Get a single rejection reason by ID.
   */
  async getRejectionReason(
    workspaceId: string,
    rejectionReasonId: string,
    idToken: string
  ): Promise<RejectionReason> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<RejectionReasonResponse>(
      `/rejection-reasons/${rejectionReasonId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get rejection reason');
    }

    return response.data.data;
  }

  /**
   * Create a new rejection reason.
   */
  async createRejectionReason(
    workspaceId: string,
    idToken: string,
    data: RejectionReasonCreateInput
  ): Promise<RejectionReason> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<RejectionReasonResponse>(`/rejection-reasons`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create rejection reason');
    }

    return response.data.data;
  }

  /**
   * Bulk create rejection reasons; duplicates skipped silently.
   * Note: backend payload key is `reasons` (not `names`).
   */
  async bulkCreateRejectionReasons(
    workspaceId: string,
    idToken: string,
    reasons: readonly string[]
  ): Promise<BulkResultData> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<BulkResultResponse>(
      `/rejection-reasons/bulk`,
      { reasons },
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to bulk create rejection reasons');
    }

    return response.data.data;
  }

  /**
   * Update a rejection reason.
   */
  async updateRejectionReason(
    workspaceId: string,
    rejectionReasonId: string,
    idToken: string,
    data: RejectionReasonUpdateInput
  ): Promise<RejectionReason> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<RejectionReasonResponse>(
      `/rejection-reasons/${rejectionReasonId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update rejection reason');
    }

    return response.data.data;
  }

  /**
   * Delete a rejection reason.
   */
  async deleteRejectionReason(
    workspaceId: string,
    rejectionReasonId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/rejection-reasons/${rejectionReasonId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const rejectionReasonService = new RejectionReasonService();
export default rejectionReasonService;
